/**
 * Users & Roles RTK Query slice — list/detail/roles/invitations endpoints.
 *
 * The pure request builders + response unwrappers live in `./requestBuilders`
 * (kept the createApi under the file budget); they're re-exported from here so
 * `./users` remains the single import site for `usersRequestBuilders`,
 * `unwrapUsers`, etc. (tests + the barrel depend on that).
 *
 * The scope discriminator (`merchantId` vs `vendorId` vs neither) is supplied
 * by the caller from `useEntityOverride()`, mirroring the legacy interceptor's
 * `entityOverride.type` branch — appended in the URL builder so the exact wire
 * is visible and lockable.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import type {
  GetUsersArgs,
  UsersSearchResponse,
  GetMerchantInvitationsArgs,
  GetVendorInvitationsArgs,
  InvitationsResponse,
  UpdateUserStatusArgs,
  ResendInvitationArgs,
  RevokeInvitationArgs,
  ForgotPasswordArgs,
  UserDetail,
  UserConfig,
  UsersConfigSchema,
  UpdateUserArgs,
  UpdateUserAttributesArgs,
  UserRoleArgs,
  GetRolesWithPermissionsArgs,
  RoleWithPermissions,
  SearchRolesArgs,
  RolesSearchResponse,
  GetRoleByIdArgs,
  GetResourcesArgs,
  CreateRoleArgs,
  UpdateRolePermissionsArgs,
  UpdateRoleNameArgs,
  PermissionSetEntry,
  EmailValidationResult,
  BulkInviteArgs,
  BulkInviteResponse,
  BulkInviteResultRow,
} from './types';
import {
  usersRequestBuilders,
  unwrapUsers,
  unwrapInvitations,
  unwrapBulkInvite,
} from './requestBuilders';

// Re-export the pure helpers so `./users` stays the single public import site
// (the api barrel + wire-shape-lock tests import them from here).
export {
  usersRequestBuilders,
  unwrapUsers,
  unwrapInvitations,
  unwrapBulkInvite,
  USERS_CONFIG_SCHEMA,
} from './requestBuilders';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Users', 'Invitations', 'User', 'UserConfig', 'Roles', 'Role'],
  endpoints: (builder) => ({
    /** GET the scoped (or admin-wide) user list; unwraps `{ users, total }`. */
    getUsers: builder.query<UsersSearchResponse, GetUsersArgs>({
      query: (args) => usersRequestBuilders.getUsers(args),
      transformResponse: unwrapUsers,
      providesTags: (result) =>
        result
          ? [
              ...result.users.map((u) => ({ type: 'Users' as const, id: u.id })),
              { type: 'Users' as const, id: 'LIST' },
            ]
          : [{ type: 'Users' as const, id: 'LIST' }],
    }),

    /** GET merchant invitations; unwraps `{ invitations, total }`. */
    getMerchantInvitations: builder.query<InvitationsResponse, GetMerchantInvitationsArgs>({
      query: (args) => usersRequestBuilders.getMerchantInvitations(args),
      transformResponse: unwrapInvitations,
      providesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),

    /** GET vendor invitations (distinct path family); unwraps the same envelope. */
    getVendorInvitations: builder.query<InvitationsResponse, GetVendorInvitationsArgs>({
      query: (args) => usersRequestBuilders.getVendorInvitations(args),
      transformResponse: unwrapInvitations,
      providesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),

    /** PATCH enable/disable a user. */
    updateUserStatus: builder.mutation<void, UpdateUserStatusArgs>({
      query: (args) => usersRequestBuilders.updateUserStatus(args),
      invalidatesTags: (_r, _e, { userId }) => [
        { type: 'Users', id: userId },
        { type: 'Users', id: 'LIST' },
      ],
    }),

    /** DELETE a user. */
    deleteUser: builder.mutation<void, { userId: string }>({
      query: (args) => usersRequestBuilders.deleteUser(args),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),

    /** POST a password-reset email for a user (admin-initiated). */
    requestForgotPassword: builder.mutation<void, ForgotPasswordArgs>({
      query: (args) => usersRequestBuilders.requestForgotPassword(args),
    }),

    /** POST resend an invitation (merchant or vendor path by entityType). */
    resendInvitation: builder.mutation<void, ResendInvitationArgs>({
      query: (args) => usersRequestBuilders.resendInvitation(args),
      invalidatesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),

    /** DELETE (revoke) an invitation (merchant or vendor path by entityType). */
    revokeInvitation: builder.mutation<void, RevokeInvitationArgs>({
      query: (args) => usersRequestBuilders.revokeInvitation(args),
      invalidatesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),

    /** GET a single user's full detail (roles[].permissionSet); identity transform. */
    getUserById: builder.query<UserDetail, { userId: string }>({
      query: (args) => usersRequestBuilders.getUserById(args),
      transformResponse: (response: UserDetail) => response,
      providesTags: (_r, _e, { userId }) => [{ type: 'User', id: userId }],
    }),

    /** GET the user's UsersConfiguration values (NotificationsCard). */
    getUserConfiguration: builder.query<UserConfig, { userId: string }>({
      query: (args) => usersRequestBuilders.getUserConfiguration(args),
      transformResponse: (response: UserConfig) => response,
      providesTags: (_r, _e, { userId }) => [{ type: 'UserConfig', id: userId }],
    }),

    /** GET the UsersConfiguration JSON schema (NotificationsCard labels + frequency enum). */
    getUserSchema: builder.query<UsersConfigSchema, void>({
      query: () => usersRequestBuilders.getUserSchema(),
      transformResponse: (response: UsersConfigSchema) => response,
    }),

    // ─── Phase 2b (edit mode) ────────────────────────────────────────────
    /**
     * GET the role-with-permissions list backing the edit Role dropdown.
     * Response is a bare `RoleWithPermissions[]` (rank-1 live); identity
     * transform made explicit so the next reader sees the unwrap.
     */
    getRolesWithPermissions: builder.query<RoleWithPermissions[], GetRolesWithPermissionsArgs>({
      query: (args) => usersRequestBuilders.getRolesWithPermissions(args),
      transformResponse: (response: RoleWithPermissions[]) => response ?? [],
    }),

    /**
     * PATCH the user's profile + notification config. Cache-neutral by design —
     * see `assignRole`/`unassignRole`; the caller invalidates once after the
     * full save sequence.
     */
    updateUser: builder.mutation<void, UpdateUserArgs>({
      query: (args) => usersRequestBuilders.updateUser(args),
    }),

    /** PUT the user's org attributes (expects 204); busts User + Users LIST. */
    updateUserAttributes: builder.mutation<void, UpdateUserAttributesArgs>({
      query: (args) => usersRequestBuilders.updateUserAttributes(args),
      invalidatesTags: (_r, _e, { userId }) => [
        { type: 'User', id: userId },
        { type: 'Users', id: userId },
        { type: 'Users', id: 'LIST' },
      ],
    }),

    /**
     * Assign / unassign a role. Cache-neutral by design: a role change is an
     * unassign-then-assign swap during which the user briefly has no role, so
     * invalidating here could cache that role-less intermediate. The callers
     * invalidate once after the swap completes instead.
     */
    assignRole: builder.mutation<void, UserRoleArgs>({
      query: (args) => usersRequestBuilders.assignRole(args),
    }),

    unassignRole: builder.mutation<void, UserRoleArgs>({
      query: (args) => usersRequestBuilders.unassignRole(args),
    }),

    /**
     * GET the role list; unwraps `{ roles, total }`. Backs both the Switch-Org
     * dropdown (P2c) and the Roles list (P3). Provides per-id + LIST `Roles`
     * tags so a delete busts the list without nuking unrelated caches.
     */
    searchRoles: builder.query<RolesSearchResponse, SearchRolesArgs>({
      query: (args) => usersRequestBuilders.searchRoles(args),
      transformResponse: (response: RolesSearchResponse | { roles?: RoleWithPermissions[] }) => ({
        roles: response.roles ?? [],
        total: (response as RolesSearchResponse).total ?? response.roles?.length ?? 0,
      }),
      providesTags: (result) =>
        result
          ? [...result.roles.map((r) => ({ type: 'Roles' as const, id: r.id })), { type: 'Roles' as const, id: 'LIST' }]
          : [{ type: 'Roles' as const, id: 'LIST' }],
    }),

    /** DELETE a role; busts the Roles list. */
    deleteRole: builder.mutation<void, { roleId: string }>({
      query: (args) => usersRequestBuilders.deleteRole(args),
      invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
    }),

    /** GET a single role's full permission set (RoleDetails view/edit). */
    getRoleById: builder.query<RoleWithPermissions, GetRoleByIdArgs>({
      query: (args) => usersRequestBuilders.getRoleById(args),
      transformResponse: (response: RoleWithPermissions) => response,
      providesTags: (_r, _e, { roleId }) => [{ type: 'Role', id: roleId }],
    }),

    /** GET the assignable resources for create-role; identity (bare array). */
    getResources: builder.query<PermissionSetEntry[], GetResourcesArgs>({
      query: (args) => usersRequestBuilders.getResources(args),
      transformResponse: (response: PermissionSetEntry[] | { resources?: PermissionSetEntry[] }) =>
        Array.isArray(response) ? response : (response.resources ?? []),
    }),

    /** POST a new role; busts the Roles list. */
    createRole: builder.mutation<{ id: string }, CreateRoleArgs>({
      query: (args) => usersRequestBuilders.createRole(args),
      invalidatesTags: [{ type: 'Roles', id: 'LIST' }],
    }),

    /** PUT a role's permissions; invalidates the role + the list. */
    updateRolePermissions: builder.mutation<void, UpdateRolePermissionsArgs>({
      query: (args) => usersRequestBuilders.updateRolePermissions(args),
      invalidatesTags: (_r, _e, { roleId }) => [
        { type: 'Role', id: roleId },
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    /** PUT a role's name; invalidates the role + the list. */
    updateRoleName: builder.mutation<void, UpdateRoleNameArgs>({
      query: (args) => usersRequestBuilders.updateRoleName(args),
      invalidatesTags: (_r, _e, { roleId }) => [
        { type: 'Role', id: roleId },
        { type: 'Roles', id: 'LIST' },
      ],
    }),

    // ─── Phase 4 (Invite User wizard) ────────────────────────────────────
    /** POST validate invite emails (merchant). Bare-array body; no cache tags. */
    validateInviteEmails: builder.mutation<EmailValidationResult, string[]>({
      query: (emails) => usersRequestBuilders.validateInviteEmails(emails),
    }),

    /** POST validate invite emails (vendor). */
    validateVendorInvitationEmails: builder.mutation<EmailValidationResult, string[]>({
      query: (emails) => usersRequestBuilders.validateVendorInvitationEmails(emails),
    }),

    /** POST the merchant bulk invite; busts the Invitations list. */
    bulkInviteSubaccount: builder.mutation<BulkInviteResultRow[], BulkInviteArgs>({
      query: (args) => usersRequestBuilders.bulkInviteSubaccount(args),
      transformResponse: (response: BulkInviteResponse) => unwrapBulkInvite(response),
      invalidatesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),

    /** POST the vendor bulk invite; busts the Invitations list. */
    bulkInviteVendor: builder.mutation<BulkInviteResultRow[], BulkInviteArgs>({
      query: (args) => usersRequestBuilders.bulkInviteVendor(args),
      transformResponse: (response: BulkInviteResponse) => unwrapBulkInvite(response),
      invalidatesTags: [{ type: 'Invitations', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetMerchantInvitationsQuery,
  useGetVendorInvitationsQuery,
  useUpdateUserStatusMutation,
  useDeleteUserMutation,
  useRequestForgotPasswordMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useGetUserByIdQuery,
  useGetUserConfigurationQuery,
  useGetUserSchemaQuery,
  useGetRolesWithPermissionsQuery,
  useUpdateUserMutation,
  useUpdateUserAttributesMutation,
  useAssignRoleMutation,
  useUnassignRoleMutation,
  useSearchRolesQuery,
  useDeleteRoleMutation,
  useGetRoleByIdQuery,
  useGetResourcesQuery,
  useCreateRoleMutation,
  useUpdateRolePermissionsMutation,
  useUpdateRoleNameMutation,
  useValidateInviteEmailsMutation,
  useValidateVendorInvitationEmailsMutation,
  useBulkInviteSubaccountMutation,
  useBulkInviteVendorMutation,
} = usersApi;
